// Caso de uso de la compañía de la sesión (CC-CONSOLE #1634).
//
// Lo leen `/apps` (AppsHub) y el menú de aplicaciones para ordenar las apps por
// grupo (`config/apps/useCaseGroups.ts`). Vive en un store para pedirlo UNA vez
// por sesión y compañía elegida (misma clave que el Sandbox:
// `token|companyId`), no una vez por pantalla.
//
// De dónde sale, en este orden:
//   1. `GET /billing/status`, si ya está en memoria (la puerta de cobro lo pide
//      al entrar): trae `useCase` y `useCaseStored`. SÓLO sirve cuando no hay
//      compañía elegida en el selector del encabezado: ese estado es el de la
//      compañía del usuario, y un administrador de plataforma con otra
//      compañía elegida agruparía por la equivocada.
//   2. `GET /company/use-case[?id=]` (services/companyUseCase.ts), con la
//      compañía del selector como hace la tarjeta del perfil.
//
// Al cambiar la compañía del selector, `companiesStore.setCompanyId` vuelve a
// pedirlo (misma mecánica que el Sandbox); al guardarlo en el perfil, la
// tarjeta escribe el valor directo (`setUseCase`).
//
// `null` = SIN caso de uso: la compañía no tiene uno guardado (`useCaseStored`
// falso), la API es anterior y no lo dice, falló, o no hay sesión. Con `null`
// nada cambia: `/apps` y el menú se ven como siempre (CC-CONSOLE y las
// compañías viejas). Nunca se adivina «transportista» por omisión, aunque la
// API normalice así el campo ausente.

import { useEffect } from "react";
import { create } from "zustand";
import { parseUseCase, type UseCase } from "../../utils/useCases";

/** Compañía elegida en el selector del encabezado ("" si ninguna). */
function selectedCompanyId(): string {
  try {
    return (localStorage.getItem("companyId") || "").trim();
  } catch {
    return "";
  }
}

/** Clave de la consulta: la sesión y la compañía elegida en el encabezado. */
export function companyUseCaseKey(): string {
  try {
    const token = localStorage.getItem("token") || "";
    if (!token) return "";
    return `${token}|${selectedCompanyId()}`;
  } catch {
    return "";
  }
}

/**
 * Caso de uso GUARDADO de una respuesta de la API, o `null`. Sólo cuenta si la
 * API afirma que está guardado (`useCaseStored: true`); una API anterior no
 * manda la bandera y entonces se trata como «sin caso de uso».
 */
export function readStoredUseCase(raw: unknown): UseCase | null {
  if (!raw || typeof raw !== "object") return null;
  const body = raw as { useCase?: unknown; useCaseStored?: unknown };
  if (body.useCaseStored !== true) return null;
  return parseUseCase(body.useCase);
}

interface CompanyUseCaseState {
  useCase: UseCase | null;
  /** Clave para la que se pidió (se marca antes de pedir: dos pantallas piden una vez). */
  loadedFor: string | null;
  /** Clave para la que la API ya contestó. */
  resolvedFor: string | null;
  load: (opts?: { force?: boolean }) => Promise<void>;
  /** Lo que acaba de guardar el perfil (PUT): se escribe directo, sin volver a pedir. */
  setUseCase: (useCase: UseCase | null) => void;
  reset: () => void;
}

// Los servicios se cargan al usarlos: traer el cliente de la API (y Firebase)
// al importar este módulo lo arrastraría a cada layout que pinta el menú y a
// cada prueba que lo monta.
const services = async () => ({
  billing: await import("../../pages/purchase/billingService"),
  company: await import("../../services/companyUseCase"),
});

async function resolveUseCase(): Promise<UseCase | null> {
  const { billing, company } = await services();
  if (!selectedCompanyId()) {
    const cached = billing.peekBillingStatus();
    if (cached && typeof cached.useCaseStored === "boolean") return readStoredUseCase(cached);
  }
  const state = await company.fetchCompanyUseCase();
  return state.stored ? state.useCase : null;
}

export const useCompanyUseCaseStore = create<CompanyUseCaseState>((set, get) => ({
  useCase: null,
  loadedFor: null,
  resolvedFor: null,

  load: async ({ force = false } = {}) => {
    const key = companyUseCaseKey();
    if (!key) {
      if (get().loadedFor !== null) set({ useCase: null, loadedFor: null, resolvedFor: null });
      return;
    }
    if (!force && get().loadedFor === key) return;
    set({ loadedFor: key });
    let next: UseCase | null = null;
    try {
      next = await resolveUseCase();
    } catch {
      // Un administrador de plataforma sin compañía (404), red caída, API vieja:
      // sin caso de uso, que es el comportamiento de siempre.
      next = null;
    }
    if (get().loadedFor === key) set({ useCase: next, resolvedFor: key });
  },

  setUseCase: (useCase) => {
    const key = companyUseCaseKey() || null;
    set({ useCase, loadedFor: key, resolvedFor: key });
  },

  reset: () => set({ useCase: null, loadedFor: null, resolvedFor: null }),
}));

export interface CompanyUseCaseView {
  /** Caso de uso guardado, o `null` (sin caso de uso, o todavía sin respuesta). */
  useCase: UseCase | null;
  /**
   * ¿Ya se sabe? Falso mientras la API contesta la primera vez en esta sesión
   * y compañía: `/apps` espera a saberlo para no pintar una vista y saltar a
   * la otra. Con la respuesta en memoria es verdadero de inmediato, y sin
   * sesión también (no hay nada que esperar).
   */
  resolved: boolean;
}

/**
 * Caso de uso de la compañía. Pide el dato al montar si hace falta; con la
 * respuesta ya en memoria (misma sesión y compañía) contesta de inmediato.
 */
export function useCompanyUseCase(): CompanyUseCaseView {
  const useCase = useCompanyUseCaseStore((s) => s.useCase);
  const resolvedFor = useCompanyUseCaseStore((s) => s.resolvedFor);
  const load = useCompanyUseCaseStore((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  const key = companyUseCaseKey();
  const resolved = !key || resolvedFor === key;
  return { useCase: resolved ? useCase : null, resolved };
}
